import { useState, useEffect, useCallback } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'
import type { AgentProxyGetSequenceDefinitionResponse } from '@/services/api/models/AgentProxyGetSequenceDefinitionResponse'
import type { AgentProxyDropSequenceResponse } from '@/services/api/models/AgentProxyDropSequenceResponse'
import { ConfirmationModal } from '@/components/common/ConfirmationModal'

interface Sequence {
  name: string
  lastValue?: number
  startValue?: number
  increment?: number
  minValue?: number
  maxValue?: number
  cache?: number
  cycle?: boolean
  owner?: string
}

interface SequenceDetailsViewProps {
  connection: DatabaseConnection
  database: string
  sequenceName: string
  schema?: string
}

export function SequenceDetailsView({ connection, database, sequenceName, schema }: SequenceDetailsViewProps) {
  const [sequence, setSequence] = useState<Sequence | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const { showToast } = useToast()

  useEffect(() => {
    const fetchSequence = async () => {
      try {
        setIsLoading(true)
        
        const response = await AgentProxyService.getSequenceDefinition(
          connection.id,
          database,
          sequenceName,
          schema
        ) as AgentProxyGetSequenceDefinitionResponse


        // Handle different response structures
        if (response.definition) {
          const def = response.definition
          setSequence({
            name: def.sequence_name || sequenceName,
            lastValue: undefined, // Current value not available in definition
            startValue: def.start_value,
            increment: def.increment_by,
            minValue: def.min_value,
            maxValue: def.max_value,
            cache: def.cache,
            cycle: def.cycle_option,
            owner: def.owned_by_table || undefined
          })
        } else if (response.status === 'error') {
          const errorMsg = response.message || 'Failed to fetch sequence definition'
          console.error('Failed to fetch sequence details:', errorMsg)
          showToast('error', `Failed to fetch sequence details: ${errorMsg}`)
        } else {
          console.error('Failed to fetch sequence details: Invalid response format - missing definition')
          showToast('error', 'Failed to fetch sequence details: Invalid response format - missing definition')
        }
      } catch (error) {
        console.error('Failed to fetch sequence details:', error)
        showToast('error', `Failed to fetch sequence details: ${error instanceof Error ? error.message : 'Unknown error'}`)
      } finally {
        setIsLoading(false)
      }
    }

    void fetchSequence()
  }, [connection.id, database, sequenceName, schema, showToast])

  const handleDelete = useCallback(async () => {
    try {
      setIsDeleting(true)
      
      const response = await AgentProxyService.dropSequence(
        connection.id,
        database,
        sequenceName,
        schema,
        true // cascade
      ) as AgentProxyDropSequenceResponse


      // Handle successful drop (response could be just a confirmation)
      if (response.status === 'success') {
        showToast('success', `Sequence "${sequenceName}" dropped successfully`)
        // Navigate back or refresh the parent view
        window.history.back()
      } else {
        const errorMsg = response.message || 'Failed to drop sequence'
        console.error('Failed to drop sequence:', errorMsg)
        showToast('error', `Failed to drop sequence: ${errorMsg}`)
      }
    } catch (error) {
      console.error('Failed to drop sequence:', error)
      showToast('error', `Failed to drop sequence: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsDeleting(false)
    }
  }, [connection.id, database, sequenceName, schema, showToast])

  const handleDeleteClick = useCallback(() => {
    setShowDeleteModal(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setShowDeleteModal(false)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    void handleDelete()
  }, [handleDelete])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!sequence) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Sequence Not Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            The sequence "{sequenceName}" could not be found.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 h-full overflow-auto">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="text-3xl mr-4">🔢</div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {sequence.name}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  PostgreSQL Sequence • {schema || 'public'} schema
                </p>
              </div>
            </div>
            
            <button type="button"
              onClick={handleDeleteClick}
              disabled={isDeleting}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Dropping...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Drop Sequence
                </>
              )}
            </button>
          </div>
        </div>

        {/* Sequence Properties */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Sequence Properties
          </h2>
          
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Current Value
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {sequence.lastValue?.toLocaleString() ?? 'N/A'}
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Start Value
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {sequence.startValue?.toLocaleString() ?? 'N/A'}
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Increment
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {sequence.increment ?? 'N/A'}
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Minimum Value
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {sequence.minValue?.toLocaleString() ?? 'N/A'}
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Maximum Value
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {sequence.maxValue?.toLocaleString() ?? 'N/A'}
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Cache Size
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {sequence.cache ?? 'N/A'}
              </p>
            </div>
            
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Cycle
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  sequence.cycle 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}>
                  {sequence.cycle ? 'Yes' : 'No'}
                </span>
              </p>
            </div>
            
            {sequence.owner && (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Owner
                </label>
                <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                  {sequence.owner}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Usage Information */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-3">
            About PostgreSQL Sequences
          </h3>
          <div className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
            <p>• Sequences generate unique numeric values, commonly used for auto-incrementing primary keys</p>
            <p>• The current value shows the last number generated by this sequence</p>
            <p>• Increment determines how much the sequence advances with each NEXTVAL() call</p>
            <p>• Cache size affects performance by pre-allocating sequence values in memory</p>
            <p>• Cycle determines whether the sequence wraps around after reaching its maximum value</p>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        title="Drop Sequence"
        message={`Are you sure you want to drop the sequence "${sequenceName}"? This action cannot be undone.`}
        confirmText="Drop Sequence"
        cancelText="Cancel"
        variant="danger"
        isDangerous={true}
      />
    </div>
  )
}
